import { JsonSchemaProperty, PRIMARY_TYPE_OPTIONS } from '../types'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Title } from '@siftscience/focus-components/text'
import { Button, IconButton } from '@siftscience/focus-components/button'
import { Delete } from '@siftscience/focus-components/icons/Delete'
import { TextInput } from '@siftscience/focus-components/input'
import { Select } from '@siftscience/focus-components/select'
import { makeStyles } from '@material-ui/core/styles'
import capitalize from '../../../../utils/capitalize'

const useStyles = makeStyles(() => ({
  actions: { display: 'flex', alignItems: 'center', gap: 8 },
  section: {
    border: '1px solid var(--focus-border, #e3e3e3)',
    borderRadius: 8,
    padding: 8,
    background: 'var(--focus-surface, #fff)'
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8
  },
  sectionTitleWrap: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    flexWrap: 'wrap'
  },
  requiredMark: { color: '#eb3b2e' },
  sectionBody: { display: 'grid', gap: 8, marginTop: 8 },
  label: { fontSize: 12, opacity: 0.7 },
  row: { display: 'grid', gap: 4 },
  select: { minWidth: 220 }
}))

export const ColumnSection = ({
  col,
  isRequired,
  onRename,
  onToggleRequired,
  onDelete,
  selectedPrimaryType,
  onChangePrimaryType
}: {
  col: JsonSchemaProperty & { uiId?: string }
  isRequired: boolean
  onRename: (oldKey: string, newKey: string, newTitle?: string) => void
  onToggleRequired: (key: string) => void
  onDelete: (key: string) => void
  selectedPrimaryType: string
  onChangePrimaryType: (next: string) => void
}) => {
  const classes = useStyles()

  const [titleValue, setTitleValue] = useState(col.title)
  const [keyValue, setKeyValue] = useState(col.key)

  const originalKeyRef = useRef(col.key)

  useEffect(() => {
    setTitleValue(col.title)
  }, [col.title])
  useEffect(() => {
    setKeyValue(col.key)
    originalKeyRef.current = col.key
  }, [col.key])

  const handleTitleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const nextTitle = e.target.value
      setTitleValue(nextTitle)
      onRename(originalKeyRef.current, originalKeyRef.current, nextTitle)
    },
    [onRename]
  )

  const handleKeyChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const nextKey = e.target.value
      setKeyValue(nextKey)
      onRename(originalKeyRef.current, nextKey, titleValue)
    },
    [onRename, titleValue]
  )

  return (
    <section className={classes.section} aria-label={`column-${col.key}`}>
      <div className={classes.sectionHeader}>
        <div className={classes.sectionTitleWrap}>
          <Title size="xsmall">{col.title || col.key}</Title>
          <code>({col.key})</code>
          {isRequired && (
            <span className={classes.requiredMark} title="required">
              *
            </span>
          )}
        </div>
        <div className={classes.actions}>
          <Button
            size="medium"
            variant={isRequired ? 'primary' : 'secondary-ghost'}
            onClick={() => onToggleRequired(col.key)}
          >
            {isRequired ? 'Required' : 'Not required'}
          </Button>
          {!isRequired && (
            <IconButton
              variant="secondary-ghost"
              onClick={() => onDelete(col.key)}
              title="Remove"
            >
              <Delete />
            </IconButton>
          )}
        </div>
      </div>

      <div className={classes.sectionBody}>
        <div className={classes.row}>
          <span className={classes.label}>Title</span>
          <TextInput
            value={titleValue}
            onChange={handleTitleChange}
            placeholder="Title"
          />
        </div>

        <div className={classes.row}>
          <span className={classes.label}>Key</span>
          <TextInput
            value={keyValue}
            onChange={handleKeyChange}
            placeholder="key"
          />
        </div>

        <div className={classes.row}>
          <span className={classes.label}>Type</span>
          <Select
            className={classes.select}
            variant="secondary"
            items={[...PRIMARY_TYPE_OPTIONS]}
            getItemLabel={item => capitalize(item as string)}
            onChange={item => onChangePrimaryType(item as string)}
            selectedItem={selectedPrimaryType}
            stretch
          />
        </div>
      </div>
    </section>
  )
}
